import { Button, SkeletonBlock, TextField, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { ResizableDivider } from '../components/ResizableDivider';
import { PageErrorState } from '../components/shared/PageErrorState';
import { useDirtyStateContext } from '../contexts/DirtyStateContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { apiFetch } from '../lib/apiClient';
import './deal-attributes-page.css';

// ─── Skeleton ─────────────────────────────────────────────────────────────────

export function DealAttributesSkeleton() {
  return (
    <div className="da-page da-page--loading" aria-busy="true" aria-label="Loading deal attributes">
      <div className="da-skeleton-nav">
        {[0, 1, 2].map((i) => (
          <SkeletonBlock key={i} className="da-skeleton__nav-item" aria-hidden="true" />
        ))}
      </div>
      <div className="da-skeleton-detail">
        <SkeletonBlock className="da-skeleton__heading" />
        {[0, 1, 2, 3].map((j) => (
          <SkeletonBlock key={j} className="da-skeleton__row" />
        ))}
      </div>
    </div>
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DealAttributeOptionSummary {
  id: number;
  valueKey: string;
  label: string;
  color: string | null;
  displayOrder: number;
  isSystem: boolean;
}

export interface DealAttributeSummary {
  id: number;
  attributeKey: string;
  label: string;
  displayOrder: number;
  isSystem: boolean;
  options: DealAttributeOptionSummary[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toSnakeCase(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

// ─── View-only option row ─────────────────────────────────────────────────────

function AttributeOptionReadRow({ option }: { option: DealAttributeOptionSummary }) {
  return (
    <div className="da-option-row da-option-row--readonly">
      <div className="da-option-row__label-group">
        <span className="da-option-row__label-text">{option.label}</span>
        {option.isSystem && <span className="da-scope-pill da-scope-pill--system">System</span>}
      </div>
    </div>
  );
}

// ─── Drag-and-drop edit row ───────────────────────────────────────────────────

interface OptionDragRowProps {
  option: DealAttributeOptionSummary;
  index: number;
  isDragOver: boolean;
  onDragStart: (idx: number) => void;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (targetIdx: number) => void;
  onDelete: (valueKey: string) => void;
  onLabelChange: (valueKey: string, label: string) => void;
}

function OptionDragRow({
  option,
  index,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDelete,
  onLabelChange,
}: OptionDragRowProps) {
  const [draft, setDraft] = React.useState(option.label);

  React.useEffect(() => {
    setDraft(option.label);
  }, [option.label]);

  function handleBlur() {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft(option.label);
    } else if (trimmed !== option.label) {
      onLabelChange(option.valueKey, trimmed);
    }
  }

  return (
    <div
      className={[
        'da-option-row',
        'da-option-row--edit',
        isDragOver ? 'da-option-row--drag-over' : '',
        option.isSystem ? 'da-option-row--system' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver(index);
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(index);
      }}
    >
      <span className="da-option-row__drag-handle" aria-hidden="true">
        ⠿
      </span>
      {option.isSystem ? (
        <div className="da-option-row__label-group">
          <span className="da-option-row__edit-input da-option-row__edit-input--system">
            {option.label}
          </span>
          <span className="da-scope-pill da-scope-pill--system">System</span>
        </div>
      ) : (
        <>
          <input
            className="da-option-row__edit-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setDraft(option.label);
                e.currentTarget.blur();
              }
            }}
            aria-label={`Edit label for ${option.label}`}
          />
          <Tooltip.Simple label="Delete option">
            <button
              className="da-option-row__delete"
              onClick={() => onDelete(option.valueKey)}
              aria-label={`Delete ${option.label}`}
            >
              ×
            </button>
          </Tooltip.Simple>
        </>
      )}
    </div>
  );
}

// ─── Add option row (dashed placeholder at bottom of edit list) ───────────────

function AddOptionRow({ onAdd }: { onAdd: (label: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState('');

  function handleAdd() {
    const trimmed = value.trim();
    if (trimmed) {
      onAdd(trimmed);
    }
    setValue('');
    setOpen(false);
  }

  function handleCancel() {
    setValue('');
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        className="da-add-option-row"
        onClick={() => setOpen(true)}
        aria-label="Add a new option"
      >
        <span className="da-add-option-row__plus">+</span>
        <span className="da-add-option-row__label">Add an option</span>
      </button>
    );
  }

  return (
    <div className="da-add-option-row da-add-option-row--open">
      <input
        className="da-option-row__edit-input"
        value={value}
        autoFocus
        placeholder="Option label…"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleAdd();
          if (e.key === 'Escape') handleCancel();
        }}
        aria-label="New option label"
      />
      <div className="da-add-option-row__actions">
        <button
          type="button"
          className="da-add-option-row__btn da-add-option-row__btn--add"
          onClick={handleAdd}
          disabled={!value.trim()}
        >
          Add
        </button>
        <button
          type="button"
          className="da-add-option-row__btn da-add-option-row__btn--cancel"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Edit pane - option row with full controls ────────────────────────────────

interface OptionEditRowProps {
  option: DealAttributeOptionSummary;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: (valueKey: string) => void;
  onMoveDown: (valueKey: string) => void;
  onDelete: (valueKey: string) => void;
  onLabelChange: (valueKey: string, label: string) => void;
}

function OptionEditRow({
  option,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onDelete,
  onLabelChange,
}: OptionEditRowProps) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(option.label);

  function handleBlur() {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setDraft(option.label);
    } else if (trimmed !== option.label) {
      onLabelChange(option.valueKey, trimmed);
    }
    setEditing(false);
  }

  return (
    <div className="da-option-row">
      <div className="da-option-row__reorder">
        <Tooltip.Simple label="Move up">
          <button
            className="da-option-row__reorder-btn"
            disabled={isFirst}
            onClick={() => onMoveUp(option.valueKey)}
            aria-label="Move up"
          >
            ↑
          </button>
        </Tooltip.Simple>
        <Tooltip.Simple label="Move down">
          <button
            className="da-option-row__reorder-btn"
            disabled={isLast}
            onClick={() => onMoveDown(option.valueKey)}
            aria-label="Move down"
          >
            ↓
          </button>
        </Tooltip.Simple>
      </div>

      <div className="da-option-row__label">
        {editing && !option.isSystem ? (
          <input
            className="da-option-row__label-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setDraft(option.label);
                setEditing(false);
              }
            }}
            autoFocus
          />
        ) : (
          <Tooltip.Simple
            label={option.isSystem ? 'System option - cannot be renamed' : 'Click to edit label'}
          >
            <button
              className="da-option-row__label-text"
              onClick={() => !option.isSystem && setEditing(true)}
              style={option.isSystem ? { cursor: 'default' } : undefined}
            >
              {option.label}
            </button>
          </Tooltip.Simple>
        )}
      </div>

      <code className="da-option-row__key">{option.valueKey}</code>

      {option.isSystem ? (
        <span className="da-scope-pill da-scope-pill--system">System</span>
      ) : (
        <Tooltip.Simple label="Delete option">
          <button
            className="da-option-row__delete"
            onClick={() => onDelete(option.valueKey)}
            aria-label={`Delete option ${option.label}`}
          >
            ×
          </button>
        </Tooltip.Simple>
      )}
    </div>
  );
}

// ─── Edit pane - right rail content ──────────────────────────────────────────

export function AttributeEditPane({
  productUuid,
  templateUuid,
  attribute,
  onMutated,
}: {
  productUuid: string;
  templateUuid: string;
  attribute: DealAttributeSummary;
  onMutated: () => void;
}) {
  const [options, setOptions] = React.useState<DealAttributeOptionSummary[]>(attribute.options);
  const [newLabel, setNewLabel] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [addError, setAddError] = React.useState<string | null>(null);
  const [blockedDelete, setBlockedDelete] = React.useState<{ optionLabel: string } | null>(null);

  const baseUrl = `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/templates/${encodeURIComponent(templateUuid)}/deal-attributes`;

  // Sync options when a different attribute is selected in the parent
  React.useEffect(() => {
    setOptions(attribute.options);
    setNewLabel('');
    setAddError(null);
    setBlockedDelete(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attribute.attributeKey]);

  async function handleAdd() {
    const trimmedLabel = newLabel.trim();
    if (!trimmedLabel) return;
    const valueKey = toSnakeCase(trimmedLabel);
    if (!valueKey) {
      setAddError('Value key cannot be empty - use alphanumeric characters.');
      return;
    }
    if (options.some((o) => o.valueKey === valueKey)) {
      setAddError('An option with that name already exists.');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      const res = await apiFetch<{ data: DealAttributeOptionSummary }>(
        `${baseUrl}/${attribute.attributeKey}/options`,
        { method: 'POST', body: JSON.stringify({ valueKey, label: trimmedLabel }) }
      );
      setOptions((prev) => [...prev, res.data]);
      setNewLabel('');
      onMutated();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add option');
    } finally {
      setAdding(false);
    }
  }

  async function handleUpdateLabel(valueKey: string, label: string) {
    const res = await apiFetch<{ data: DealAttributeOptionSummary }>(
      `${baseUrl}/${attribute.attributeKey}/options/${valueKey}`,
      { method: 'PUT', body: JSON.stringify({ label }) }
    );
    setOptions((prev) => prev.map((o) => (o.valueKey === valueKey ? res.data : o)));
    onMutated();
  }

  async function handleDelete(valueKey: string) {
    const opt = options.find((o) => o.valueKey === valueKey);
    try {
      await apiFetch<void>(`${baseUrl}/${attribute.attributeKey}/options/${valueKey}`, {
        method: 'DELETE',
      });
      setOptions((prev) => prev.filter((o) => o.valueKey !== valueKey));
      onMutated();
    } catch (err: unknown) {
      const status =
        err != null && typeof err === 'object' && 'status' in err
          ? (err as { status: number }).status
          : 0;
      if (status === 409) {
        setBlockedDelete({ optionLabel: opt?.label ?? valueKey });
        return;
      }
      throw err;
    }
  }

  function handleMoveUp(valueKey: string) {
    const idx = options.findIndex((o) => o.valueKey === valueKey);
    if (idx <= 0) return;
    const reordered = [...options];
    const tmp = reordered[idx]!;
    reordered[idx] = reordered[idx - 1]!;
    reordered[idx - 1] = tmp;
    void handleReorder(reordered);
  }

  function handleMoveDown(valueKey: string) {
    const idx = options.findIndex((o) => o.valueKey === valueKey);
    if (idx < 0 || idx >= options.length - 1) return;
    const reordered = [...options];
    const tmp = reordered[idx]!;
    reordered[idx] = reordered[idx + 1]!;
    reordered[idx + 1] = tmp;
    void handleReorder(reordered);
  }

  async function handleReorder(reordered: DealAttributeOptionSummary[]) {
    setOptions(reordered.map((o, i) => ({ ...o, displayOrder: i })));
    await apiFetch<void>(`${baseUrl}/${attribute.attributeKey}/options/order`, {
      method: 'PUT',
      body: JSON.stringify({ orderedValueKeys: reordered.map((o) => o.valueKey) }),
    });
    onMutated();
  }

  return (
    <div className="da-edit-pane">
      <div className="da-edit-pane__header">
        <span className="da-edit-pane__title">{attribute.label}</span>
        <span className="da-edit-pane__hint">Click a label to rename · drag arrows to reorder</span>
      </div>

      <div className="da-option-list">
        {options.length === 0 && <p className="da-option-list__empty">No options yet.</p>}
        {options.map((opt, idx) => (
          <OptionEditRow
            key={opt.valueKey}
            option={opt}
            isFirst={idx === 0}
            isLast={idx === options.length - 1}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            onDelete={handleDelete}
            onLabelChange={handleUpdateLabel}
          />
        ))}
      </div>

      <div className="da-add-option">
        <TextField
          value={newLabel}
          onChange={(e) => {
            setNewLabel(e.target.value);
            setAddError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleAdd();
          }}
          placeholder="New option label…"
          aria-label={`New option label for ${attribute.label}`}
          disabled={adding}
        />
        <Button onClick={() => void handleAdd()} disabled={adding || !newLabel.trim()} size="sm">
          Add
        </Button>
      </div>

      {addError && <p className="da-add-option__error">{addError}</p>}

      {blockedDelete && (
        <div className="da-blocked-notice" role="alert">
          <p className="da-blocked-notice__text">
            <strong>{blockedDelete.optionLabel}</strong> is assigned to active deals and cannot be
            deleted. Reassign or clear those deals first.
          </p>
          <button
            className="da-blocked-notice__close"
            onClick={() => setBlockedDelete(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main section (master-detail) ────────────────────────────────────────────

export function DealAttributesSection({
  productUuid,
  templateUuid,
  excludeAttributeKeys = ['deal_category'],
}: {
  productUuid: string;
  templateUuid: string;
  /**
   * Attribute keys to hide from this view. Defaults to ['deal_category'] because
   * deal categories are promoted to their own first-class "Deal Categories" tab;
   * this section is the home for the remaining (category-agnostic) custom picklists.
   */
  excludeAttributeKeys?: string[];
}) {
  const [attributes, setAttributes] = React.useState<DealAttributeSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [retryCount, setRetryCount] = React.useState(0);
  const [localSelectedKey, setLocalSelectedKey] = React.useState<string | null>(null);
  const [navWidth, setNavWidth] = React.useState(240);
  const [isEditing, setIsEditing] = React.useState(false);
  const [localOptions, setLocalOptions] = React.useState<DealAttributeOptionSummary[]>([]);
  const [savedOptions, setSavedOptions] = React.useState<DealAttributeOptionSummary[]>([]);
  const [dragOverIdx, setDragOverIdx] = React.useState<number | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const dragIndexRef = React.useRef<number | null>(null);
  const tempIdRef = React.useRef(0);

  // Nav CRUD state
  const [isAddingAttr, setIsAddingAttr] = React.useState(false);
  const [newAttrLabel, setNewAttrLabel] = React.useState('');
  const [navMutating, setNavMutating] = React.useState(false);
  const [navError, setNavError] = React.useState<string | null>(null);
  // Detail pane attribute CRUD state
  const [attrNameDraft, setAttrNameDraft] = React.useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const openEditOnSelectRef = React.useRef(false);

  const { confirmIfDirty } = useDirtyStateContext();

  const baseUrl = `/api/workspace/admin/products/${productUuid}/templates/${templateUuid}/deal-attributes`;

  // Dirty = in edit mode and localOptions differ from the saved snapshot
  const isDirty =
    isEditing &&
    JSON.stringify(localOptions.map((o) => ({ k: o.valueKey, l: o.label }))) !==
      JSON.stringify(savedOptions.map((o) => ({ k: o.valueKey, l: o.label })));

  useUnsavedChanges(isDirty, 'deal-attributes', 'Deal attribute options');

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiFetch<{ data: DealAttributeSummary[] }>(baseUrl)
      .then((res) => {
        if (!cancelled) {
          const data = res.data.filter((a) => !excludeAttributeKeys.includes(a.attributeKey));
          setAttributes(data);
          // Keep current selection if still valid, otherwise fall back to first
          setLocalSelectedKey((prev) => {
            if (prev !== null && data.some((a) => a.attributeKey === prev)) return prev;
            return data[0]?.attributeKey ?? null;
          });
        }
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load deal attributes.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // excludeAttributeKeys is joined to a stable primitive so a new array identity
    // per render does not re-trigger the fetch (it only changes when contents change).
  }, [baseUrl, retryCount, excludeAttributeKeys.join(',')]);

  // Sync localOptions when the selected attribute changes.
  // We intentionally omit `attributes` from deps - mutations update localOptions directly
  // and sync back to `attributes` for nav counts; we don't want a re-fetch to clobber edits.
  React.useEffect(() => {
    const attr = attributes.find((a) => a.attributeKey === localSelectedKey) ?? null;
    const opts = attr?.options ?? [];
    setLocalOptions(opts);
    setSavedOptions(opts);
    setIsEditing(openEditOnSelectRef.current);
    openEditOnSelectRef.current = false;
    setSaveError(null);
    setAttrNameDraft(attr?.label ?? '');
    setDeleteConfirmOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSelectedKey]);

  if (loading) {
    return <DealAttributesSkeleton />;
  }

  if (error) {
    return <PageErrorState message={error} onRetry={() => setRetryCount((c) => c + 1)} />;
  }

  const selectedAttr = attributes.find((a) => a.attributeKey === localSelectedKey) ?? null;

  // ── Helpers ─────────────────────────────────────────────────────────────────

  // ── Local-only edit handlers (no API calls - all deferred to handleSave) ────

  function handleAddOption(label: string) {
    const tempKey = `__new__${++tempIdRef.current}`;
    const newOpt: DealAttributeOptionSummary = {
      id: -tempIdRef.current,
      valueKey: tempKey,
      label,
      color: null,
      displayOrder: localOptions.length,
      isSystem: false,
    };
    setLocalOptions((prev) => [...prev, newOpt]);
  }

  function handleDeleteOption(valueKey: string) {
    const opt = localOptions.find((o) => o.valueKey === valueKey);
    if (opt?.isSystem) return;
    setLocalOptions((prev) => prev.filter((o) => o.valueKey !== valueKey));
  }

  function handleLabelChange(valueKey: string, label: string) {
    const opt = localOptions.find((o) => o.valueKey === valueKey);
    if (opt?.isSystem) return;
    setLocalOptions((prev) => prev.map((o) => (o.valueKey === valueKey ? { ...o, label } : o)));
  }

  // ── Save - reconcile localOptions vs savedOptions and write to API ───────────

  async function handleSave() {
    if (!selectedAttr) return;
    setIsSaving(true);
    setSaveError(null);
    const attrKey = selectedAttr.attributeKey;
    try {
      // 1. Delete options removed during editing (skip temp-only options)
      const toDelete = savedOptions.filter(
        (s) => !localOptions.some((l) => l.valueKey === s.valueKey)
      );
      for (const opt of toDelete) {
        await apiFetch<void>(`${baseUrl}/${attrKey}/options/${encodeURIComponent(opt.valueKey)}`, {
          method: 'DELETE',
        });
      }

      // 2. Rename options whose label changed
      for (const opt of localOptions) {
        if (opt.valueKey.startsWith('__new__')) continue;
        const original = savedOptions.find((s) => s.valueKey === opt.valueKey);
        if (original && original.label !== opt.label) {
          await apiFetch<{ data: DealAttributeOptionSummary }>(
            `${baseUrl}/${attrKey}/options/${encodeURIComponent(opt.valueKey)}`,
            { method: 'PUT', body: JSON.stringify({ label: opt.label }) }
          );
        }
      }

      // 3. Create new options, track temp → real key
      const tempToReal = new Map<string, string>();
      for (const opt of localOptions) {
        if (!opt.valueKey.startsWith('__new__')) continue;
        const valueKey = toSnakeCase(opt.label);
        if (!valueKey) throw new Error(`Cannot derive a valid key for option "${opt.label}".`);
        const res = await apiFetch<{ data: DealAttributeOptionSummary }>(
          `${baseUrl}/${attrKey}/options`,
          { method: 'POST', body: JSON.stringify({ valueKey, label: opt.label }) }
        );
        tempToReal.set(opt.valueKey, res.data.valueKey);
      }

      // 4. Reorder if anything changed
      const finalKeys = localOptions.map((o) =>
        o.valueKey.startsWith('__new__')
          ? (tempToReal.get(o.valueKey) ?? toSnakeCase(o.label))
          : o.valueKey
      );
      const savedKeys = savedOptions.map((s) => s.valueKey);
      const orderChanged =
        toDelete.length > 0 ||
        tempToReal.size > 0 ||
        JSON.stringify(finalKeys) !== JSON.stringify(savedKeys);
      if (orderChanged) {
        await apiFetch<void>(`${baseUrl}/${attrKey}/options/order`, {
          method: 'PUT',
          body: JSON.stringify({ orderedValueKeys: finalKeys }),
        });
      }

      // 5. Re-fetch authoritative state
      const res = await apiFetch<{ data: DealAttributeSummary[] }>(baseUrl);
      setAttributes(res.data);
      const fresh = res.data.find((a) => a.attributeKey === attrKey);
      const freshOpts = fresh?.options ?? [];
      setLocalOptions(freshOpts);
      setSavedOptions(freshOpts);
      setIsEditing(false);
    } catch (err) {
      const status =
        err != null && typeof err === 'object' && 'status' in err
          ? (err as { status: number }).status
          : 0;
      if (status === 409) {
        setSaveError(
          'One or more options are assigned to active deals and cannot be deleted. Un-delete them or remove them from deals first.'
        );
      } else {
        setSaveError(err instanceof Error ? err.message : 'Failed to save changes.');
      }
    } finally {
      setIsSaving(false);
    }
  }

  // ── Drag handlers ────────────────────────────────────────────────────────────

  function handleDragStart(idx: number) {
    dragIndexRef.current = idx;
  }

  function handleDragOver(idx: number) {
    setDragOverIdx(idx);
  }

  function handleDragLeave() {
    setDragOverIdx(null);
  }

  function handleDrop(targetIdx: number) {
    const fromIdx = dragIndexRef.current;
    setDragOverIdx(null);
    dragIndexRef.current = null;
    if (fromIdx === null || fromIdx === targetIdx) return;
    const reordered = [...localOptions];
    const [item] = reordered.splice(fromIdx, 1);
    reordered.splice(targetIdx, 0, item!);
    setLocalOptions(reordered);
  }

  // ── Nav attribute CRUD ──────────────────────────────────────────────────────

  async function handleCreateAttribute() {
    const label = newAttrLabel.trim();
    if (!label || navMutating) return;
    setNavMutating(true);
    setNavError(null);
    try {
      const res = await apiFetch<{ data: DealAttributeSummary }>(baseUrl, {
        method: 'POST',
        body: JSON.stringify({ label }),
      });
      setAttributes((prev) => [...prev, res.data]);
      openEditOnSelectRef.current = true;
      setLocalSelectedKey(res.data.attributeKey);
      setNewAttrLabel('');
      setIsAddingAttr(false);
    } catch (err) {
      setNavError(err instanceof Error ? err.message : 'Failed to create attribute.');
    } finally {
      setNavMutating(false);
    }
  }

  async function handleRenameAttribute(attributeKey: string, label: string) {
    if (!label.trim()) return;
    try {
      const res = await apiFetch<{ data: DealAttributeSummary }>(
        `${baseUrl}/${encodeURIComponent(attributeKey)}`,
        { method: 'PATCH', body: JSON.stringify({ label: label.trim() }) }
      );
      setAttributes((prev) =>
        prev.map((a) => (a.attributeKey === attributeKey ? { ...a, label: res.data.label } : a))
      );
      setAttrNameDraft(res.data.label);
    } catch {
      // Restore draft to the last saved label on failure
      setAttrNameDraft(attributes.find((a) => a.attributeKey === attributeKey)?.label ?? '');
    }
  }

  async function handleDeleteAttribute(attributeKey: string) {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await apiFetch(`${baseUrl}/${encodeURIComponent(attributeKey)}`, { method: 'DELETE' });
      const remaining = attributes.filter((a) => a.attributeKey !== attributeKey);
      setAttributes(remaining);
      setDeleteConfirmOpen(false);
      setLocalSelectedKey(remaining[0]?.attributeKey ?? null);
    } catch (err) {
      setNavError(err instanceof Error ? err.message : 'Failed to delete attribute.');
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="da-page">
      <div className="da-master-detail">
        <nav className="da-nav" aria-label="Attribute list" style={{ width: `${navWidth}px` }}>
          {attributes.map((attr) => (
            <button
              key={attr.attributeKey}
              className={[
                'da-nav__item',
                localSelectedKey === attr.attributeKey ? 'da-nav__item--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setLocalSelectedKey(attr.attributeKey)}
              aria-current={localSelectedKey === attr.attributeKey ? 'true' : undefined}
            >
              <span className="da-nav__label">{attr.label}</span>
              <span className="da-nav__count">{attr.options.length}</span>
            </button>
          ))}

          {/* Add attribute */}
          {isAddingAttr ? (
            <div className="da-nav__add-row">
              <input
                className="da-nav__rename-input"
                value={newAttrLabel}
                autoFocus
                placeholder="Attribute name…"
                onChange={(e) => setNewAttrLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleCreateAttribute();
                  if (e.key === 'Escape') {
                    setIsAddingAttr(false);
                    setNewAttrLabel('');
                  }
                }}
                disabled={navMutating}
                aria-label="New attribute name"
              />
              <div className="da-nav__add-actions">
                <button
                  className="da-nav__add-confirm"
                  disabled={!newAttrLabel.trim() || navMutating}
                  onClick={() => void handleCreateAttribute()}
                >
                  Add
                </button>
                <button
                  className="da-nav__add-cancel"
                  onClick={() => {
                    setIsAddingAttr(false);
                    setNewAttrLabel('');
                    setNavError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              className="da-nav__add-attr-btn"
              onClick={() => setIsAddingAttr(true)}
              aria-label="Add attribute"
            >
              <span aria-hidden="true">+</span> Add attribute
            </button>
          )}
          {navError && <p className="da-nav__error">{navError}</p>}
        </nav>

        <ResizableDivider
          direction="vertical"
          onResize={(delta) => setNavWidth((w) => Math.max(140, Math.min(360, w + delta)))}
          ariaLabel="Resize attribute list"
        />

        <div className="da-detail">
          {selectedAttr ? (
            <>
              <div className="da-detail__header">
                <div className="da-detail__title-group">
                  {isEditing && !selectedAttr.isSystem ? (
                    <input
                      className="da-detail__title-input"
                      value={attrNameDraft}
                      onChange={(e) => setAttrNameDraft(e.target.value)}
                      onBlur={() => {
                        const trimmed = attrNameDraft.trim();
                        if (!trimmed) {
                          setAttrNameDraft(selectedAttr.label);
                          return;
                        }
                        if (trimmed !== selectedAttr.label) {
                          void handleRenameAttribute(selectedAttr.attributeKey, trimmed);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') {
                          setAttrNameDraft(selectedAttr.label);
                          e.currentTarget.blur();
                        }
                      }}
                      aria-label="Attribute name"
                      placeholder="Attribute name"
                    />
                  ) : (
                    <span className="da-detail__title-label">{selectedAttr.label}</span>
                  )}
                  <span
                    className={`da-scope-pill da-scope-pill--${selectedAttr.isSystem ? 'system' : 'workspace'}`}
                  >
                    {selectedAttr.isSystem ? 'System' : 'Workspace'}
                  </span>
                </div>
                {isEditing ? (
                  <div className="da-detail__header-actions">
                    <button
                      type="button"
                      className="da-detail__edit-btn da-detail__edit-btn--cancel"
                      disabled={isSaving}
                      onClick={() => {
                        void (async () => {
                          if (isDirty && !(await confirmIfDirty('deal-attributes'))) return;
                          setLocalOptions(savedOptions);
                          setSaveError(null);
                          setIsEditing(false);
                        })();
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="da-detail__edit-btn da-detail__edit-btn--done"
                      disabled={isSaving || !isDirty}
                      onClick={() => void handleSave()}
                    >
                      {isSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="da-detail__edit-btn"
                    onClick={() => {
                      setSavedOptions(localOptions);
                      setSaveError(null);
                      setIsEditing(true);
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="da-option-list">
                {localOptions.length === 0 && !isEditing && (
                  <p className="da-option-list__empty">No options configured.</p>
                )}
                {isEditing
                  ? localOptions.map((opt, idx) => (
                      <OptionDragRow
                        key={opt.valueKey}
                        option={opt}
                        index={idx}
                        isDragOver={dragOverIdx === idx}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onDelete={handleDeleteOption}
                        onLabelChange={handleLabelChange}
                      />
                    ))
                  : localOptions.map((opt) => (
                      <AttributeOptionReadRow key={opt.valueKey} option={opt} />
                    ))}
                {isEditing && <AddOptionRow onAdd={handleAddOption} />}
              </div>
              {saveError && <p className="da-add-option__error">{saveError}</p>}

              <div className="da-detail__footer">
                {!selectedAttr.isSystem && (
                  <button
                    className="da-detail__delete-btn"
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </>
          ) : (
            <p className="da-detail__empty">Select an attribute to view its options.</p>
          )}
        </div>
      </div>

      {/* ── Delete attribute dialog ─────────────────────────────────────── */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete attribute?"
        confirmKeyword={selectedAttr?.label ?? ''}
        description={
          <>
            <strong>{selectedAttr?.label}</strong> and all its options will be permanently removed.
            This cannot be undone.
          </>
        }
        isPending={isDeleting}
        onConfirm={() => {
          if (selectedAttr) void handleDeleteAttribute(selectedAttr.attributeKey);
        }}
      />
    </div>
  );
}
